import { useState } from 'react';
import { SlideContent } from '../types/content';

interface SlidePreviewProps {
  slide: SlideContent;
}

export default function SlidePreview({ slide }: SlidePreviewProps) {
  const [videoError, setVideoError] = useState(false);
  const designVariant = slide.designVariant || 'classic';

  const renderBackground = () => {
    if (!slide || !slide.background) {
      return (
        <div className="absolute inset-0 w-full h-full bg-gray-200"></div>
      );
    }

    if (slide.background.type === 'image') {
      return (
        <div
          className="absolute inset-0 w-full h-full opacity-60 bg-cover bg-center"
          style={{ backgroundImage: `url(${slide.background.url})` }}
        />
      );
    } else if (slide.background.type === 'video') {
      return (
        <div className="absolute inset-0 w-full h-full opacity-60">
          <video
            src={slide.background.url}
            className="w-full h-full object-cover"
            autoPlay
            loop
            muted
            playsInline
            poster={slide.background.fallbackImage}
            onError={() => setVideoError(true)}
          />
          {videoError && slide.background.fallbackImage && (
            <div
              className="absolute inset-0 w-full h-full bg-cover bg-center"
              style={{ backgroundImage: `url(${slide.background.fallbackImage})` }}
            />
          )}
        </div>
      );
    }

    return null;
  };

  const renderMediaContent = (className: string = '') => {
    if (!slide.media) return null;

    return (
      <>
        {slide.media.type === 'youtube' ? (
          <div className={`relative w-full h-full bg-gray-800 flex items-center justify-center ${className}`}>
            <div className="absolute inset-0 opacity-50 bg-cover bg-center"
              style={{ backgroundImage: `url(https://i.ytimg.com/vi/${slide.media.url}/hqdefault.jpg)` }}>
            </div>
            <div className="relative z-10 w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
              <div className="w-0 h-0 border-t-[8px] border-t-transparent border-l-[12px] border-l-white border-b-[8px] border-b-transparent ml-1"></div>
            </div>
          </div>
        ) : slide.media.type === 'video' ? (
          <div className={`w-full h-full bg-gray-800 flex items-center justify-center ${className}`}>
            <span className="text-white text-xs">Video Preview</span>
          </div>
        ) : (
          <img
            src={slide.media.url}
            alt={slide.title}
            className={`w-full h-full object-cover ${className}`}
          />
        )}
      </>
    );
  };

  const renderClassicLayout = () => (
    <div className="p-4 h-full flex items-center">
      <div className={`w-full ${slide.hideImage ? 'flex justify-center' : 'grid grid-cols-2 gap-4'}`}>
        <div className={`text-white space-y-2 ${slide.hideImage ? 'text-center max-w-md' : ''}`}>
          <span className="inline-block bg-golf-orange px-2 py-1 rounded-full text-xs font-semibold mb-1">
            {slide.subtitle}
          </span>
          <h3 className="text-lg font-bold leading-tight">
            {slide.title}
          </h3>
          <p className="text-xs text-golf-sand line-clamp-2">
            {slide.description}
          </p>
          <div className="pt-2">
            <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${
              slide.buttonVariant === 'primary'
                ? 'bg-golf-orange text-white'
                : slide.buttonVariant === 'secondary'
                ? 'bg-golf-gray text-white'
                : 'border border-golf-orange text-golf-orange'
            }`}>
              {slide.buttonText}
            </span>
          </div>
        </div>

        {!slide.hideImage && slide.media && (
          <div className="hidden sm:block">
            <div className="h-full flex items-center justify-center">
              <div className="w-full h-32 rounded-lg overflow-hidden">
                {renderMediaContent()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderModernLayout = () => (
    <div className="relative h-full flex items-end p-4">
      {!slide.hideImage && slide.media && (
        <div className="absolute top-4 right-4 w-40 h-24 rounded-lg overflow-hidden shadow-2xl border-2 border-white/20">
          {renderMediaContent()}
        </div>
      )}

      <div className={`text-white space-y-2 ${slide.hideImage ? 'w-full text-center' : 'w-2/3'}`}>
        <span className="inline-block bg-golf-orange px-2 py-1 rounded-full text-xs font-semibold mb-1">
          {slide.subtitle}
        </span>
        <h3 className="text-lg font-bold leading-tight">
          {slide.title}
        </h3>
        <p className="text-xs text-golf-sand line-clamp-2">
          {slide.description}
        </p>
        <div className="pt-2">
          <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${
            slide.buttonVariant === 'primary'
              ? 'bg-golf-orange text-white'
              : slide.buttonVariant === 'secondary'
              ? 'bg-golf-gray text-white'
              : 'border border-golf-orange text-golf-orange'
          }`}>
            {slide.buttonText}
          </span>
        </div>
      </div>
    </div>
  );

  const renderMinimalistLayout = () => (
    <div className="h-full flex items-center justify-center p-6">
      <div className="text-center text-white space-y-3 max-w-lg">
        <span className="inline-block bg-golf-orange px-3 py-1 rounded-full text-xs font-semibold">
          {slide.subtitle}
        </span>
        <h3 className="text-2xl font-bold leading-tight">
          {slide.title}
        </h3>
        <p className="text-sm text-golf-sand">
          {slide.description}
        </p>
        {!slide.hideImage && slide.media && (
          <div className="pt-2 flex justify-center">
            <div className="w-48 h-28 rounded-lg overflow-hidden shadow-lg">
              {renderMediaContent()}
            </div>
          </div>
        )}
        <div className="pt-2">
          <span className={`inline-block px-4 py-2 text-xs font-medium rounded-full ${
            slide.buttonVariant === 'primary'
              ? 'bg-golf-orange text-white'
              : slide.buttonVariant === 'secondary'
              ? 'bg-golf-gray text-white'
              : 'border border-golf-orange text-golf-orange'
          }`}>
            {slide.buttonText}
          </span>
        </div>
      </div>
    </div>
  );

  const renderBoldLayout = () => (
    <div className="relative h-full">
      {!slide.hideImage && slide.media && (
        <div className="absolute inset-0">
          {renderMediaContent('opacity-30')}
        </div>
      )}

      <div className="relative h-full flex items-center justify-center p-4">
        <div className="text-center text-white space-y-2 max-w-2xl">
          <span className="inline-block bg-golf-orange px-3 py-1 rounded-full text-xs font-semibold mb-1">
            {slide.subtitle}
          </span>
          <h3 className="text-2xl font-bold leading-tight drop-shadow-lg">
            {slide.title}
          </h3>
          <p className="text-sm text-white drop-shadow-md">
            {slide.description}
          </p>
          <div className="pt-3">
            <span className={`inline-block px-5 py-2 text-sm font-medium rounded-full shadow-lg ${
              slide.buttonVariant === 'primary'
                ? 'bg-golf-orange text-white'
                : slide.buttonVariant === 'secondary'
                ? 'bg-golf-gray text-white'
                : 'border-2 border-white text-white bg-white/10 backdrop-blur-sm'
            }`}>
              {slide.buttonText}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderLayout = () => {
    switch (designVariant) {
      case 'modern':
        return renderModernLayout();
      case 'minimalist':
        return renderMinimalistLayout();
      case 'bold':
        return renderBoldLayout();
      case 'classic':
      default:
        return renderClassicLayout();
    }
  };

  return (
    <div className="relative h-64 bg-black overflow-hidden rounded-lg">
      {renderBackground()}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-black/70" />
      <div className="relative h-full">
        {renderLayout()}
      </div>
    </div>
  );
}